import { useNotifications } from '@/hooks/useNotifications';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, Mail, MessageSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/lib/auth';

interface NotificationRowProps {
  label: string;
  description: string;
  emailField: string;
  inappField: string;
  emailChecked: boolean;
  inappChecked: boolean;
  onEmailChange: (checked: boolean) => void;
  onInappChange: (checked: boolean) => void;
}

const NotificationRow = ({
  label,
  description,
  emailField,
  inappField,
  emailChecked,
  inappChecked,
  onEmailChange,
  onInappChange,
}: NotificationRowProps) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-4 border-b border-border last:border-0 last:pb-0">
      <div className="flex-1 space-y-1">
        <Label className="text-base font-medium">{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      
      <div className="flex items-center gap-6 md:ml-4">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor={emailField} className="text-sm font-normal cursor-pointer">
            Email
          </Label>
          <Switch
            id={emailField}
            checked={emailChecked}
            onCheckedChange={onEmailChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor={inappField} className="text-sm font-normal cursor-pointer">
            In-App
          </Label>
          <Switch
            id={inappField}
            checked={inappChecked}
            onCheckedChange={onInappChange}
          />
        </div>
      </div>
    </div>
  );
};

export const NotificationPreferences = () => {
  const { preferences, loading, updatePreferences } = useNotifications();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const handleToggle = async (field: string, value: boolean) => {
    await updatePreferences({ [field]: value });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Preferences
          </CardTitle>
          <CardDescription>
            Control how you receive notifications for each event type
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Control how you receive notifications for each event type
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Jobs & RFQs Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Jobs & RFQs
          </h4>
          
          <NotificationRow
            label="RFQ Matches"
            description="Receive notifications when new RFQs match your capabilities"
            emailField="email_rfq_matches"
            inappField="inapp_rfq_matches"
            emailChecked={preferences?.email_rfq_matches ?? true}
            inappChecked={preferences?.inapp_rfq_matches ?? true}
            onEmailChange={(checked) => handleToggle('email_rfq_matches', checked)}
            onInappChange={(checked) => handleToggle('inapp_rfq_matches', checked)}
          />

          <NotificationRow
            label="Bid Updates"
            description="Get notified when bids are submitted or when you win/lose bids"
            emailField="email_bid_updates"
            inappField="inapp_bid_updates"
            emailChecked={preferences?.email_bid_updates ?? true}
            inappChecked={preferences?.inapp_bid_updates ?? true}
            onEmailChange={(checked) => handleToggle('email_bid_updates', checked)}
            onInappChange={(checked) => handleToggle('inapp_bid_updates', checked)}
          />

          <NotificationRow
            label="RFQ Questions"
            description="Get notified when vendors ask questions about your RFQs"
            emailField="email_rfq_questions"
            inappField="inapp_rfq_questions"
            emailChecked={preferences?.email_rfq_questions ?? true}
            inappChecked={preferences?.inapp_rfq_questions ?? true}
            onEmailChange={(checked) => handleToggle('email_rfq_questions', checked)}
            onInappChange={(checked) => handleToggle('inapp_rfq_questions', checked)}
          />
        </div>

        {/* Purchase Orders Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Purchase Orders
          </h4>
          
          <NotificationRow
            label="Purchase Order Issued"
            description="Get notified when a purchase order is issued for jobs you've won"
            emailField="email_po_issued"
            inappField="inapp_po_issued"
            emailChecked={preferences?.email_po_issued ?? true}
            inappChecked={preferences?.inapp_po_issued ?? true}
            onEmailChange={(checked) => handleToggle('email_po_issued', checked)}
            onInappChange={(checked) => handleToggle('inapp_po_issued', checked)}
          />

          <NotificationRow
            label="PO Change Orders"
            description="Get notified when change orders are requested, approved, or declined on purchase orders"
            emailField="email_po_adjustments"
            inappField="inapp_po_adjustments"
            emailChecked={preferences?.email_po_adjustments ?? true}
            inappChecked={preferences?.inapp_po_adjustments ?? true}
            onEmailChange={(checked) => handleToggle('email_po_adjustments', checked)}
            onInappChange={(checked) => handleToggle('inapp_po_adjustments', checked)}
          />

          <NotificationRow
            label="Purchase Order Cancellations"
            description="Get notified about cancellation requests, vendor responses, fee approvals, and refund processing"
            emailField="email_po_cancellations"
            inappField="inapp_po_cancellations"
            emailChecked={preferences?.email_po_cancellations ?? true}
            inappChecked={preferences?.inapp_po_cancellations ?? true}
            onEmailChange={(checked) => handleToggle('email_po_cancellations', checked)}
            onInappChange={(checked) => handleToggle('inapp_po_cancellations', checked)}
          />

          <NotificationRow
            label="ACH Payment Updates"
            description="Get notified when ACH payments are submitted and when they successfully settle"
            emailField="email_ach_payments"
            inappField="inapp_ach_payments"
            emailChecked={preferences?.email_ach_payments ?? true}
            inappChecked={preferences?.inapp_ach_payments ?? true}
            onEmailChange={(checked) => handleToggle('email_ach_payments', checked)}
            onInappChange={(checked) => handleToggle('inapp_ach_payments', checked)}
          />
        </div>

        {/* Milestones Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Milestones
          </h4>
          
          <NotificationRow
            label="Milestone Reminders"
            description="Receive reminders when milestone deadlines are approaching"
            emailField="email_milestone_reminders"
            inappField="inapp_milestone_reminders"
            emailChecked={preferences?.email_milestone_reminders ?? true}
            inappChecked={preferences?.inapp_milestone_reminders ?? true}
            onEmailChange={(checked) => handleToggle('email_milestone_reminders', checked)}
            onInappChange={(checked) => handleToggle('inapp_milestone_reminders', checked)}
          />
        </div>

        {/* Files & Documents Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Files & Documents
          </h4>
          
          <NotificationRow
            label="File Uploads"
            description="Get notified when files are uploaded to your jobs"
            emailField="email_file_uploads"
            inappField="inapp_file_uploads"
            emailChecked={preferences?.email_file_uploads ?? true}
            inappChecked={preferences?.inapp_file_uploads ?? true}
            onEmailChange={(checked) => handleToggle('email_file_uploads', checked)}
            onInappChange={(checked) => handleToggle('inapp_file_uploads', checked)}
          />

          <NotificationRow
            label="Shipment Updates"
            description="Get notified when vendors add shipments to your jobs"
            emailField="email_shipment_updates"
            inappField="inapp_shipment_updates"
            emailChecked={preferences?.email_shipment_updates ?? true}
            inappChecked={preferences?.inapp_shipment_updates ?? true}
            onEmailChange={(checked) => handleToggle('email_shipment_updates', checked)}
            onInappChange={(checked) => handleToggle('inapp_shipment_updates', checked)}
          />
        </div>

        {/* Account & Organization Section */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
            Account & Organization
          </h4>
          
          <NotificationRow
            label="Enrollment & Certification Updates"
            description="Receive updates about your vendor enrollment status and certification documentation reviews"
            emailField="email_verification_updates"
            inappField="inapp_verification_updates"
            emailChecked={preferences?.email_verification_updates ?? true}
            inappChecked={preferences?.inapp_verification_updates ?? true}
            onEmailChange={(checked) => handleToggle('email_verification_updates', checked)}
            onInappChange={(checked) => handleToggle('inapp_verification_updates', checked)}
          />

          <NotificationRow
            label="Organization Invitations"
            description="Get notified when you're invited to join an organization"
            emailField="email_organization_invites"
            inappField="inapp_organization_invites"
            emailChecked={preferences?.email_organization_invites ?? true}
            inappChecked={preferences?.inapp_organization_invites ?? true}
            onEmailChange={(checked) => handleToggle('email_organization_invites', checked)}
            onInappChange={(checked) => handleToggle('inapp_organization_invites', checked)}
          />

          <NotificationRow
            label="Review Reminders"
            description="Receive reminders to submit reviews after job completion"
            emailField="email_review_reminders"
            inappField="inapp_review_reminders"
            emailChecked={preferences?.email_review_reminders ?? true}
            inappChecked={preferences?.inapp_review_reminders ?? true}
            onEmailChange={(checked) => handleToggle('email_review_reminders', checked)}
            onInappChange={(checked) => handleToggle('inapp_review_reminders', checked)}
          />

          <NotificationRow
            label="System Updates"
            description="Stay informed about important system announcements and updates"
            emailField="email_system_updates"
            inappField="inapp_system_updates"
            emailChecked={preferences?.email_system_updates ?? true}
            inappChecked={preferences?.inapp_system_updates ?? true}
            onEmailChange={(checked) => handleToggle('email_system_updates', checked)}
            onInappChange={(checked) => handleToggle('inapp_system_updates', checked)}
          />
        </div>

        {/* Admin Notifications Section - Only for admins */}
        {isAdmin && (
          <>
            <Separator className="my-6" />
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Admin Notifications
              </h4>
              
              <NotificationRow
                label="Payment Approvals"
                description="Notifications when vendor payouts or client refunds require approval"
                emailField="email_payment_approvals"
                inappField="inapp_payment_approvals"
                emailChecked={preferences?.email_payment_approvals ?? true}
                inappChecked={preferences?.inapp_payment_approvals ?? true}
                onEmailChange={(checked) => handleToggle('email_payment_approvals', checked)}
                onInappChange={(checked) => handleToggle('inapp_payment_approvals', checked)}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
